import { type FC } from "react";
import { Link } from "react-router-dom";

import {
  type WakeBlockPresentation,
  wakeBlockPresentation,
} from "../blackboardModels";

// ---------------------------------------------------------------------------
// WakeBlockCallout — the wizard's non-truncated explanation of an active wake
// block, rendered right below the lead-wake stage row.
//
// StageRow truncates its detail line, so the explanation and any CTA cannot
// live there. The toast viewport has no live-region semantics, so this callout
// carries the accessible announcement itself: role="status" + aria-live polite
// (important, but not a focus-stealing alert) + aria-atomic so the whole block
// is read as one message.
// ---------------------------------------------------------------------------

const CTA_CLASS =
  "inline-flex w-fit items-center border border-amber-500/50 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-amber-800 transition-colors hover:bg-amber-500/10 dark:text-amber-200";

export const WakeBlockCallout: FC<{
  reason: string | null;
  /** Server-scheduled automatic retry, ISO timestamp; shown only for reasons
   * whose policy says Arti retries on its own. */
  wakeBlockedUntil: string | null;
  /** Whether this user may administer organization provider configuration. */
  canManageOrgSettings: boolean;
}> = ({ reason, wakeBlockedUntil, canManageOrgSettings }) => {
  const presentation = wakeBlockPresentation(reason);
  const retryText =
    presentation.showsScheduledRetry && wakeBlockedUntil
      ? formatScheduledRetry(wakeBlockedUntil)
      : null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="flex flex-col gap-2 border border-amber-500/40 bg-amber-500/5 px-3 py-2"
    >
      <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-amber-800 dark:text-amber-200">
        {presentation.badgeLabel}
      </p>
      <p className="text-xs text-amber-900/90 dark:text-amber-100/80">
        {presentation.explanation}
      </p>
      {retryText && (
        <p className="font-mono text-[10px] text-amber-800/80 dark:text-amber-200/70">
          {retryText}
        </p>
      )}
      <WakeBlockCalloutAction
        canManageOrgSettings={canManageOrgSettings}
        presentation={presentation}
      />
      {presentation.isUnknownReason && presentation.reasonCode && (
        <p
          className="font-mono text-[9px] uppercase tracking-[0.12em] text-amber-800/60 dark:text-amber-200/50"
          title={presentation.reasonCode}
        >
          diagnostic code: {presentation.reasonCode}
        </p>
      )}
    </div>
  );
};

const WakeBlockCalloutAction: FC<{
  canManageOrgSettings: boolean;
  presentation: WakeBlockPresentation;
}> = ({ canManageOrgSettings, presentation }) => {
  if (presentation.action === "manage_credits") {
    return (
      <Link className={CTA_CLASS} to="/settings?tab=billing">
        Manage credits
      </Link>
    );
  }
  if (presentation.action === "configure_provider") {
    return canManageOrgSettings ? (
      <Link className={CTA_CLASS} to="/settings?tab=api-keys">
        Open API keys
      </Link>
    ) : (
      <p className="text-xs text-amber-900/90 dark:text-amber-100/80">
        {presentation.unauthorizedNote}
      </p>
    );
  }
  return null;
};

/** Absolute clock time, not a relative countdown: the dialog can stay open for
 * minutes and a once-computed "in 5 minutes" goes stale. */
function formatScheduledRetry(isoTimestamp: string): string | null {
  const date = new Date(isoTimestamp);
  if (!Number.isFinite(date.getTime())) {
    return "Arti will retry automatically.";
  }
  return `Arti will retry at ${date.toLocaleString([], {
    dateStyle: "medium",
    timeStyle: "short",
  })}.`;
}
